import React, { useEffect, useRef, useState } from "react";
import { TableInfo } from "api";
import { ColumnDetail } from "@avamae/table";
import { Formik, Form, useField, FormikProps, useFormikContext } from "formik";
import { Dropdown, SelectOption } from "components/dropdown/Dropdown";
import DatePicker from "react-datepicker";
import * as Yup from "yup";
import { userLocale } from "helpers/userLocale";
import { placeholder } from "@babel/types";
import { debounce } from "lodash";
import { stringToDate } from "helpers/stringToDate";
import { MultiDropdown } from "components/dropdown/MultiDropdown";
import { compareNewAndExistingFilters } from "pages/Documents/helpers/HeaderDocuComp";

export type Operator =
  | "EQ"
  | "NEQ"
  | "SW"
  | "EW"
  | "CONTAINS"
  | "GT"
  | "GEQ"
  | "LT"
  | "LEQ"
  | "INSTRINGARRAY"
  | "BETWEEN";

export type OperatorOptions = { label: string; key: Operator };

type InstringFilter = Extract<Filter, { operator: "INSTRINGARRAY" }>;

export type Filter =
  | {
    columnKey: string;
    operator: Omit<Operator, "INSTRINGARRAY">;
    value: string;
  }
  | {
    columnKey: string;
    operator: "INSTRINGARRAY";
    value: string[];
  };
export type Filters = {
  columnKey: string;
  filters: Filter[];
}[];

export type FilterMetadata = { details: any | null; filterType: string };

type FilterBuilderProps = {
  table: TableInfo;
  onDone(): void;
  hiddenFields?: string[];
  handleCancel: () => void;
  commsTab?: boolean;
};

const debouncedOnChange = debounce(
  (value: string, handleOnChange: (value: string) => void) => {
    if (handleOnChange) {
      handleOnChange(value);
    }
  },
  700
);

export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  table,
  onDone,
  hiddenFields,
  handleCancel,
  commsTab,
}) => {
  const FormikProps = useRef<FormikProps<any>>(null);

  const columns: ColumnDetail[] =
    table && table.data ? table.data.details.columns : [];
  const [keyVal, setKeyVal] = React.useState(0);

  const hidden: string[] = hiddenFields
    ? hiddenFields.map(c => c.toLowerCase())
    : [];

  const [metadata, initialValues] = React.useMemo(() => {
    let meta: Record<string, FilterMetadata> = {};
    let values: Record<string, { columnKey: string; filters: Filter[] }> = {};
    table.data?.details.columns.forEach(entry => {
      if (entry.bFilterable) meta[entry.columnKey] = entry.filterMetadata;
      if (entry.bFilterable)
        values[entry.columnKey] = table.data?.details.filters.find(
          f => f.columnKey === entry.columnKey
        ) ?? { columnKey: entry.columnKey, filters: [] };
    });
    return [meta, values];
  }, [table]);

  function handleSubmit(
    values: Record<string, { columnKey: string; filters: Filter[] }>
  ) {
    const filters: Filter[] = Object.values(values).flatMap(
      filt => filt.filters
    );

    const existingFilters = table.data?.details?.filters;

    const existingFiltersMatchNewFilters = compareNewAndExistingFilters(
      filters,
      existingFilters
    );

    // if (!existingFiltersMatchNewFilters) {
    table.data?.actions.setFilters(filters);
    // }
    onDone();
  }

  if (columns) {
    return (
      <Formik
        innerRef={FormikProps}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {formik => {
          return (
            <Form>
              <div className="Content">
                <div className="Form">
                  <div className="Filters">
                    {columns
                      .filter(
                        x =>
                          x.bFilterable &&
                          !hidden.includes(x.columnKey.toString().toLowerCase())
                      )
                      .map(x => {
                        switch (x.filterMetadata?.filterType) {
                          case "TextContains":
                            return (
                              <div key={x.columnKey.toString()}>
                                <TextContainsField
                                  name={x.columnKey.toString()}
                                  label={x.labelValue}
                                />
                              </div>
                            );
                          case "Amount":
                            return (
                              <div key={x.columnKey.toString()}>
                                <AmountField
                                  name={x.columnKey.toString()}
                                  label={x.labelValue}
                                />
                              </div>
                            );
                          case "SingleChoice":
                            return (
                              <div key={x.columnKey.toString()}>
                                <SingleChoiceField
                                  name={x.columnKey.toString()}
                                  label={x.labelValue}
                                  keyVal={keyVal}
                                  possibleValues={
                                    x.filterMetadata.details?.PossibleValues ??
                                    []
                                  }
                                />
                              </div>
                            );
                          case "MultipleChoice":
                            return (
                              <div key={x.columnKey.toString()}>
                                <MultipleChoiceField
                                  name={x.columnKey.toString()}
                                  label={x.labelValue}
                                  keyVal={keyVal}
                                  possibleValues={
                                    x.filterMetadata.details?.PossibleValues ??
                                    []
                                  }
                                  initialValues={
                                    initialValues[x.columnKey.toString()]
                                      .filters
                                  }
                                />
                              </div>
                            );
                          case "CurrencyAmount:Range":
                            return (
                              <div key={x.columnKey.toString()}>
                                <CurrencyRangeField
                                  name={x.columnKey.toString()}
                                  label={x.labelValue}
                                // currency={true}
                                />
                              </div>
                            );
                          case "Amount:Range":
                            return (
                              <div key={x.columnKey.toString()}>
                                <AmountRangeField
                                  name={x.columnKey.toString()}
                                  label={x.labelValue}
                                />
                              </div>
                            );
                          case "DateTime:Range":
                            return (
                              <div key={x.columnKey.toString()}>
                                <DateTimeRangeField
                                  name={x.columnKey.toString()}
                                  label={x.labelValue}
                                />
                              </div>
                            );

                          default:
                            const metadataData =
                              metadata[x.columnKey.toString()];

                            if (metadataData && metadataData.filterType) {
                              return (
                                <div key={x.columnKey.toString()}>
                                  {x.columnKey} - {metadataData.filterType}
                                </div>
                              );
                            }
                        }
                      })}
                  </div>
                </div>

                {!commsTab && (
                  <div className="Buttons">
                    {/* NOTE: Made this a div instead of a button as the onClick would trigger when the user presses enter, instead of triggering the submit function */}
                    <div className="Btn BtnCancel" onClick={handleCancel}>
                      Cancel
                    </div>

                    <button type="submit" className="Btn">
                      Apply
                    </button>
                  </div>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    );
  }

  return <div className="Filters"></div>;
};

const numberValidator = Yup.number();

const numericalOperators = [
  { value: "CONTAINS", label: "Contains" },
  { value: "EQ", label: "Equals" },
  { value: "GT", label: "Greater than" },
  { value: "GEQ", label: "Greater or equal to" },
  { value: "LT", label: "Less than" },
  { value: "LEQ", label: "Less than or equal to" },
];

const AmountField: React.FC<{ name: string; label: string }> = ({
  name,
  label,
}) => {
  const [operator, setOperator] = useState(numericalOperators[0]);
  const [field, , helpers] = useField(name);
  const { filters } = field?.value ?? { filters: [] };

  const value = filters[0]?.value ?? "";

  function handleChange(e: React.ChangeEvent<any>) {
    e.preventDefault();
    const newValue = e.target.value;
    if (e.target.value !== "" && !numberValidator.isValidSync(e.target.value))
      return;
    const filters: Filter[] = [];

    if (newValue.length > 0) {
      filters.push({
        columnKey: field.value.columnKey,
        operator: operator.value,
        value: newValue,
      });
    }
    helpers.setValue({
      columnKey: field.value.columnKey,
      filters,
    });
  }

  return (
    <div className="FormBox FormSideBySide">
      <div className="FormLabel">
        <label>{label}</label>
      </div>
      <div className="InputContainers">
        <div className={`FormDropdownField`}>
          <div className={"FormField"}>
            <Dropdown
              key={`${name}Operator`}
              value={operator}
              options={numericalOperators}
              onChange={(e: SelectOption) => {
                setOperator(e);
              }}
              roundedRectangle
            />
          </div>
        </div>
        <div className="FormField">
          <input
            key={`${name}Value`}
            className="EditBox"
            {...field}
            value={value}
            onChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

const textOperators = [
  { value: "CONTAINS", label: "Contains" },
  { value: "EQ", label: "Equals" },
];

const TextContainsField: React.FC<{ name: string; label: string }> = ({
  name,
  label,
}) => {
  const [field, meta, helpers] = useField(name);
  const [operator, setOperator] = useState(
    meta.initialValue?.filters && meta.initialValue.filters.length > 0
      ? textOperators.find(
        o => o.value === meta.initialValue.filters[0]?.operator
      ) ?? textOperators[0]
      : textOperators[0]
  );

  const { filters } = field?.value ?? { filters: [] };
  const value = filters[0]?.value ?? "";

  useEffect(() => {
    const currentFilter = filters
      ? filters.filter((item: any) => item.columnKey === field.value.columnKey)
      : null;

    if (currentFilter && currentFilter.length === 1) {
      const newFilter = { ...currentFilter[0], operator: operator.value };

      let newFilters = filters.filter(
        (item: any) => item.columnKey !== field.value.columnKey
      );
      newFilters.push(newFilter);

      helpers.setValue({
        columnKey: field.value.columnKey,
        filters: newFilters,
      });
    }
  }, [operator]);

  function handleChange(e: React.ChangeEvent<any>) {
    const newValue = e.target.value;
    const filters: Filter[] = [];

    if (newValue.length > 0) {
      filters.push({
        columnKey: field.value.columnKey,
        operator: operator.value,
        value: newValue,
      });
    }
    helpers.setValue({
      columnKey: field.value.columnKey,
      filters,
    });
  }

  return (
    <div className="FormBox FormSideBySide">
      <div className="FormLabel">
        <label>{label}</label>
      </div>
      <div className="InputContainers">
        <div className={`FormDropdownField`}>
          <div className={"FormField"}>
            <Dropdown
              key={`${name}Operator`}
              value={operator}
              options={textOperators}
              onChange={(e: SelectOption) => {
                setOperator(e);
              }}
              roundedRectangle
            />
          </div>
        </div>
        <div className="FormField">
          <input
            className="EditBox"
            {...field}
            value={value}
            onChange={handleChange}
          />
        </div>
      </div>
    </div>
  );
};

export function SingleChoiceField(props: {
  name: string;
  label: string;
  keyVal: number;
  possibleValues: { label: string; value: React.ReactText }[];
  placeholder?: string;
  isClearable?: boolean;
}) {
  const [field, , helpers] = useField(props.name);
  const { filters } = field?.value ?? { filters: [] };
  const value = filters[0]?.value;

  function handleChange(selectedOption: SelectOption) {
    const filters: Filter[] = [];

    if (selectedOption && selectedOption.value != null) {
      filters.push({
        columnKey: field.value?.columnKey ?? props.name,

        operator: "EQ",
        value: String(selectedOption.value),
      });
    }

    helpers.setValue({
      columnKey: field.value?.columnKey ?? props.name,

      filters: filters,
    });
  }
  return (
    <div className="FormBox FormSideBySide SingleChoice">
      <div className="FormLabel">
        <label>{props.label}</label>
      </div>
      <div className="FormField">
        <Dropdown
          key={`${props.name}${props.keyVal}`}
          value={
            props.possibleValues.find(v => v.value === value)
              ? props.possibleValues.find(v => v.value === value)
              : null
          }
          options={props.possibleValues}
          onChange={handleChange}
          roundedRectangle
          placeholder={props.placeholder}
          isClearable={props.isClearable}
        />
      </div>
    </div>
  );
}

function MultipleChoiceField(props: {
  name: string;
  label: string;
  keyVal: number;
  possibleValues: SelectOption[];
  initialValues: Filter[];
}) {
  const initVals = props.initialValues[0] as InstringFilter;
  const selected: SelectOption[] = props.possibleValues.filter(
    p => initVals && initVals.value.find(i => i === p.value)
  );
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[] | null>(
    selected
  );
  const [field, , helpers] = useField(props.name);

  function handleChange(selectedOption: SelectOption[]) {
    const filters: Filter[] = [];
    const vals = selectedOption && selectedOption.map(o => String(o.value));
    setSelectedOptions(selectedOption);
    if (vals) {
      filters.push({
        columnKey: field.value.columnKey,
        operator: "INSTRINGARRAY",
        value: vals,
      });
    }
    helpers.setValue({
      columnKey: field.value.columnKey,
      filters,
    });
  }

  return (
    <div className="FormBox FormSideBySide">
      <div className="FormLabel">
        <label>{props.label}</label>
      </div>
      <div className="FormField">
        <MultiDropdown
          customClassName="MultiChoiceFilterDropdown"
          key={`${props.name}${props.keyVal}`}
          value={selectedOptions}
          options={props.possibleValues}
          onChange={handleChange}
          styles={{
            multiValue: (base: any) => {
              return {
                ...base,
                backgroundColor: "#DEEBFF",
                color: "#4C9AFF",
              };
            },
            multiValueLabel: (base: any) => {
              return {
                ...base,
                color: "#4C9AFF",
              };
            },
          }}
          roundedRectangle
        />
      </div>
    </div>
  );
}

export const formatDate = (date: Date | null) => {
  if (date) {
    let formattedDate = date.toISOString();
    return formattedDate.replace("Z", "").split(".")[0];
  } else {
    return null;
  }
};

export function AmountRangeField(props: { name: string; label: string }) {
  const [field, , helpers] = useField(props.name);
  const { filters } = field?.value ?? { filters: [] };
  const value = filters[0]?.value ?? "";
  let fieldValues = value.split("TO");

  function handleChangeFirst(e: React.ChangeEvent<any>) {
    e.preventDefault();
    let newValue = e.target.value;
    if (newValue !== "" && !numberValidator.isValidSync(newValue)) return;

    if (
      (newValue === "" && fieldValues[1] === "") ||
      (newValue === "" && fieldValues[1] === "null") ||
      (newValue === null && fieldValues[1] === null)
    ) {
      helpers.setValue({
        columnKey: field.value.columnKey,
        filters: [],
      });
      return;
    } else {
      const filters: Filter[] = [];
      if (newValue === "") {
        newValue = null;
      }
      filters.push({
        columnKey: field.value.columnKey,
        operator: "BETWEEN",
        value: `${newValue}TO${fieldValues[1]}`,
      });
      helpers.setValue({
        columnKey: field.value.columnKey,
        filters,
      });
    }
  }
  function handleChangeSecond(e: React.ChangeEvent<any>) {
    e.preventDefault();
    let newValue = e.target.value;
    if (newValue !== "" && !numberValidator.isValidSync(newValue)) return;
    if (
      (newValue === "" && fieldValues[0] === "") ||
      (newValue === "" && fieldValues[0] === "null") ||
      (newValue === null && fieldValues[0] === null)
    ) {
      helpers.setValue({
        columnKey: field.value.columnKey,
        filters: [],
      });
      return;
    } else {
      const filters: Filter[] = [];

      if (newValue === "") {
        newValue = null;
      }
      filters.push({
        columnKey: field.value.columnKey,
        operator: "BETWEEN",
        value: `${fieldValues[0]}TO${newValue}`,
      });
      helpers.setValue({
        columnKey: field.value.columnKey,
        filters,
      });
    }
  }
  return (
    <div className="FormBox FormSideBySide DateTimeRange">
      <div className="FormLabel">
        <label>{props.label}</label>
      </div>
      <div className="FormField">
        <div className="DateFields">
          <div className="StartingDate">
            <input
              className="EditBox"
              {...field}
              type="number"
              value={fieldValues[0]}
              onChange={handleChangeFirst}
              placeholder="From"
            />
          </div>

          <div className="EndingDate">
            <input
              className="EditBox"
              {...field}
              type="number"
              value={fieldValues[1]}
              onChange={handleChangeSecond}
              placeholder="To"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function CurrencyRangeField(props: { name: string; label: string }) {
  const [field, , helpers] = useField(props.name);
  const { filters } = field?.value ?? { filters: [] };
  const value = filters[0]?.value ?? "";
  let fieldValues = value.split("TO");
  const [fromValue, setFromValue] = useState<string>("");
  const [toValue, setToValue] = useState<string>("");

  useEffect(() => {
    if (fieldValues[0]) {
      setFromValue(fieldValues[0]);
    }
  }, [fieldValues[0]]);
  useEffect(() => {
    if (fieldValues[1]) {
      setToValue(fieldValues[1]);
    }
  }, [fieldValues[1]]);

  function handleChangeFirst(newValue: string | number) {
    if (
      (newValue === "" && fieldValues[1] === "0.00") ||
      (newValue === "" && fieldValues[1] === "null") ||
      (newValue === "" && fieldValues[1] === "")
    ) {
      helpers.setValue({
        columnKey: field.value.columnKey,
        filters: [],
      });
      return;
    } else {
      const filters: Filter[] = [];
      if (typeof newValue === "string" && newValue !== "") {
        newValue = Number(newValue);
        filters.push({
          columnKey: field.value.columnKey,
          operator: "BETWEEN",
          value: `${newValue.toFixed(2)}TO${fieldValues[1]}`,
        });
        helpers.setValue({
          columnKey: field.value.columnKey,
          filters,
        });
      } else if (newValue === "") {
        filters.push({
          columnKey: field.value.columnKey,
          operator: "BETWEEN",
          value: `${null}TO${fieldValues[1]}`,
        });
        helpers.setValue({
          columnKey: field.value.columnKey,
          filters,
        });
        return;
      }
    }
  }
  function handleChangeSecond(newValue: string | number) {
    if (
      (newValue === "" && fieldValues[0] === "0.00") ||
      (newValue === "" && fieldValues[0] === null) ||
      (newValue === "" && fieldValues[0] === "")
    ) {
      helpers.setValue({
        columnKey: field.value.columnKey,
        filters: [],
      });
      return;
    } else {
      const filters: Filter[] = [];
      if (typeof newValue === "string" && newValue !== "") {
        newValue = Number(newValue);
        filters.push({
          columnKey: field.value.columnKey,
          operator: "BETWEEN",
          value: `${fieldValues[0]}TO${newValue.toFixed(2)}`,
        });
        helpers.setValue({
          columnKey: field.value.columnKey,
          filters,
        });
      } else if (newValue === "") {
        filters.push({
          columnKey: field.value.columnKey,
          operator: "BETWEEN",
          value: `${fieldValues[0]}TO${null}`,
        });
      }
    }
  }

  const debounceChange1 = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newValue = e.target.value;
    setFromValue(newValue);
    if (newValue !== "" && !numberValidator.isValidSync(newValue)) return;
    debouncedOnChange(newValue, handleChangeFirst);
  };

  const debounceChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newValue = e.target.value;
    setToValue(newValue);
    if (newValue !== "" && !numberValidator.isValidSync(newValue)) return;
    debouncedOnChange(newValue, handleChangeSecond);
  };

  return (
    <div className="FormBox FormSideBySide DateTimeRange">
      <div className="FormLabel">
        <label>{props.label}</label>
      </div>
      <div className="FormField">
        <div className="DateFields">
          <div className="StartingDate">
            <input
              className="EditBox"
              {...field}
              type="number"
              value={fromValue}
              onChange={e => debounceChange1(e)}
              placeholder="From"
            />
          </div>

          <div className="EndingDate">
            <input
              className="EditBox"
              {...field}
              type="number"
              value={toValue}
              onChange={e => debounceChange2(e)}
              placeholder="To"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export function DateTimeRangeField(props: {
  name: string;
  label: string;
  range?: boolean;
  disableTyping?: boolean;
  isClearable?: boolean;
}) {
  const [field, , helpers] = useField(props.name);

  const { filters } = field?.value ?? { filters: [] };
  const value = filters[0]?.value ?? "";
  let values = value.split("TO");
  if (values.length !== 2) values = ["NULL", "NULL"];

  const [parsedStartDate, setParsedStartDate] = useState<Date | null>(null);
  const [parsedEndDate, setParsedEndDate] = useState<Date | null>(null);

  function handleChangeFirst(date: Date | null) {
    const filters: Filter[] = [];

    if (date) {

      let datepickerDate = date;

      let parsedDate: Date | null = null;

      let parsedDate2: string | null = null;

      const offset = date ? date.getTimezoneOffset() * 60000 : null;

      if (date && offset !== null) {
        const tempDate = new Date(date.setHours(0, 0, 0));

        datepickerDate = tempDate;
        parsedDate = new Date(tempDate.getTime() - offset);
      }

      if (parsedDate) {
        const year = parsedDate.getUTCFullYear();
        const month = parsedDate.getUTCMonth() + 1;
        const day = parsedDate.getUTCDate();

        const yearString = `${year}`;
        let monthString = `${month}`;
        let dayString = `${day}`;

        if (month < 10) {
          monthString = `0${month}`;
        }

        if (day < 10) {
          dayString = `0${day}`;
        }

        parsedDate2 = `${yearString}-${monthString}-${dayString}T00:00:00`;
      }

      if (datepickerDate) {
        setParsedStartDate(datepickerDate);
      }

      if (parsedDate2) {
        filters.push({
          columnKey: field.value.columnKey,
          operator: "BETWEEN",
          value: `${parsedDate2}TO${values[1]}`,
        });
        helpers.setValue({
          columnKey: field.value.columnKey,
          filters,
        });
      }
    }
    else {
      setParsedStartDate(null); // Clear the start date state
      values[0] = "NULL";       // Update the start part of the range to indicate no start date

      if (values[1] != "NULL") {
        filters.push({
          columnKey: field.value.columnKey,
          operator: "BETWEEN",
          value: `NULLTO${values[1]}`, // Assuming values[1] holds the end date
        });
      }
      else {
        filters.push({
          columnKey: field.value.columnKey,
          operator: "NULL",
          value: `NULLTONULL`,
        });
      }

      helpers.setValue({
        columnKey: field.value.columnKey,
        filters,
      });
    }
  }

  function handleChangeSecond(date: Date | null) {
    const filters: Filter[] = [];

    if (date) {

      let datepickerDate = date;

      let parsedDate: Date | null = null;

      let parsedDate2: string | null = null;

      const offset = date ? date.getTimezoneOffset() * 60000 : null;

      if (date && offset !== null) {
        const tempDate = new Date(date.setHours(23, 59, 59));

        datepickerDate = tempDate;
        parsedDate = new Date(tempDate.getTime() - offset);
      }

      if (parsedDate) {
        const year = parsedDate.getUTCFullYear();
        const month = parsedDate.getUTCMonth() + 1;
        const day = parsedDate.getUTCDate();

        const yearString = `${year}`;
        let monthString = `${month}`;
        let dayString = `${day}`;

        if (month < 10) {
          monthString = `0${month}`;
        }

        if (day < 10) {
          dayString = `0${day}`;
        }

        parsedDate2 = `${yearString}-${monthString}-${dayString}T23:59:59`;
      }

      if (datepickerDate) {
        setParsedEndDate(parsedDate);
      }

      if (parsedDate2) {
        filters.push({
          columnKey: field.value.columnKey,
          operator: "BETWEEN",
          value: `${values[0]}TO${parsedDate2}`,
        });
        helpers.setValue({
          columnKey: field.value.columnKey,
          filters,
        });
      }
    }
    else {
      setParsedEndDate(null); // Clear the end date state
      values[1] = "NULL";     // Update the end part of the range to indicate no end date

      if (values[0] != "NULL") {
        filters.push({
          columnKey: field.value.columnKey,
          operator: "BETWEEN",
          value: `${values[0]}TONULL`, // Assuming values[0] holds the start date
        });
      }
      else {
        filters.push({
          columnKey: field.value.columnKey,
          operator: "NULL",
          value: `NULLTONULL`,
        });
      }

      helpers.setValue({
        columnKey: field.value.columnKey,
        filters,
      });
    }
  }

  const changeDateFormat = (date: any): string => {
    return stringToDate(date).toString();
  };

  const parseValueDate = (value: string) => {
    let newDate = value;

    if (newDate !== "NULL") {
      newDate = newDate.split("T")[0];

      newDate = changeDateFormat(newDate);
    }

    return newDate;
  };

  const clearStartDate = () => {
    handleChangeFirst(null);
  };

  const clearEndDate = () => {
    handleChangeSecond(null);
  };

  const debouncedHandleDateChangeFirst = debounce(
    (
      date
    ) =>
      handleChangeFirst(date),
    1000 // Wait 1 seconds before procesing date change
  );

  const debouncedHandleDateChangeSecond = debounce(
    (
      date
    ) =>
      handleChangeSecond(date),
    1000 // Wait 1 seconds before procesing date change
  );

  useEffect(() => {
    let parsedStartDateValue = parseValueDate(values[0]);

    const startDate =
      parsedStartDateValue === "NULL" ? null : new Date(parsedStartDateValue);

    if (
      !parsedStartDate ||
      (parsedStartDate &&
        startDate &&
        parsedStartDate.getTime() !== startDate.getTime())
    ) {
      setParsedStartDate(startDate);
    }

    if (!startDate) {
      setParsedStartDate(null);
    }

    let parsedEndDateValue = parseValueDate(values[1]);

    const endDate =
      parsedEndDateValue === "NULL" ? null : new Date(parsedEndDateValue);

    if (
      !parsedEndDate ||
      (parsedEndDate &&
        endDate &&
        parsedEndDate.getTime() !== endDate.getTime())
    ) {
      setParsedEndDate(endDate);
    }

    if (!endDate) {
      setParsedEndDate(null);
    }
  }, [values]);

  return (
    <div className="FormBox FormSideBySide DateTimeRange">
      <div className="FormLabel">
        <label>{props.label}</label>
      </div>
      <div className="FormField">
        <div className="DateFields">
          <div className="StartingDate">
            <DatePicker
              className="EditBox"
              dateFormat="dd-MMM-yyyy"
              endDate={parsedEndDate}
              locale={userLocale}
              onChange={debouncedHandleDateChangeFirst}
              placeholderText="Start date"
              selected={parsedStartDate}
              showPopperArrow={false}
              startDate={parsedStartDate}
              timeFormat="p"
            />
            {props.isClearable && parsedStartDate &&
              removeDate(() =>
                clearStartDate()
              )}
          </div>

          <div className="EndingDate">
            <DatePicker
              className="EditBox"
              dateFormat="dd-MMM-yyyy"
              endDate={parsedEndDate}
              locale={userLocale}
              minDate={parsedStartDate}
              onChange={debouncedHandleDateChangeSecond}
              placeholderText="End date"
              selected={parsedEndDate}
              showPopperArrow={false}
              startDate={parsedStartDate}
            />
            {props.isClearable && parsedEndDate &&
              removeDate(() =>
                clearEndDate()
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

export const removeDate = (handleClick: () => void) => {
  return (
    <div
      className="RemoveIcon"
      onClick={() => {
        handleClick();
      }}
    >
      <svg
        height="14"
        width="14"
        viewBox="0 0 20 20"
        aria-hidden="true"
        style={{ fill: "#58595b" }}
        focusable="false"
      >
        <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
      </svg>
    </div>
  );

};